import {
  ROLE_ADMIN,
  ROLE_BUSINESS,
  ROLE_CLIENT,
  normalizeRole,
} from "./roles";

export const DEFAULT_CAPABILITIES = Object.freeze({
  canViewDashboard: false,
  canViewBusinesses: false,
  canEditBusinesses: false,
  canViewDashboardReports: false,
  canCreateBusinesses: false,
  canManageUsers: false,
  canManageClients: false,
  canManageSettings: false,
  canExportTranscripts: false,
  canViewConversationBrowser: false,
  canUseChatBotDemo: false,
});

const ADMIN_CAPABILITIES = Object.freeze({
  ...DEFAULT_CAPABILITIES,
  canViewDashboard: true,
  canViewBusinesses: true,
  canEditBusinesses: true,
  canViewDashboardReports: true,
  canCreateBusinesses: true,
  canManageUsers: true,
  canManageClients: true,
  canManageSettings: true,
  canExportTranscripts: true,
  canViewConversationBrowser: true,
  canUseChatBotDemo: true,
});

const SCOPED_USER_CAPABILITIES = Object.freeze({
  ...DEFAULT_CAPABILITIES,
  canViewDashboard: true,
  canViewBusinesses: true,
  canEditBusinesses: true,
  canViewDashboardReports: true,
  canExportTranscripts: true,
});

export const buildRoleCapabilities = (role) => {
  const normalizedRole = normalizeRole(role);

  if (normalizedRole === ROLE_ADMIN) {
    return ADMIN_CAPABILITIES;
  }

  if (normalizedRole === ROLE_CLIENT || normalizedRole === ROLE_BUSINESS) {
    return SCOPED_USER_CAPABILITIES;
  }

  return DEFAULT_CAPABILITIES;
};

export const resolveCapabilities = ({
  role,
  capabilities,
  authBypassed,
} = {}) => {
  if (authBypassed) {
    return { ...ADMIN_CAPABILITIES };
  }

  const resolvedCapabilities = {
    ...buildRoleCapabilities(role),
    ...(capabilities || {}),
  };

  // Transcript export now follows dashboard access for all supported roles.
  // Keep that behavior resilient if a stale /me/access payload still sends
  // canExportTranscripts: false while dashboard access is otherwise granted.
  if (resolvedCapabilities.canViewDashboard) {
    resolvedCapabilities.canExportTranscripts = true;
  }

  return resolvedCapabilities;
};

export const normalizeAccessInput = (access = {}) => {
  if (typeof access === "string") {
    return {
      role: normalizeRole(access),
      capabilities: null,
      authBypassed: false,
    };
  }

  return {
    role: normalizeRole(access?.role || access?.userType || access?.roleName),
    capabilities: access?.capabilities || null,
    authBypassed: Boolean(access?.authBypassed),
  };
};

export const resolveAccessCapabilities = (access = {}) =>
  resolveCapabilities(normalizeAccessInput(access));

const normalizeScopedId = (value) => {
  return value?.toString().trim() || null;
};

const normalizeAllowedBusinessIds = ({
  allowedBusinessIds,
  isBusiness,
  businessId,
  hasGlobalBusinessAccess,
}) => {
  if (Array.isArray(allowedBusinessIds)) {
    const normalizedIds = allowedBusinessIds
      .map(normalizeScopedId)
      .filter(Boolean);

    if (!normalizedIds.length && isBusiness && businessId) {
      return [businessId];
    }

    return normalizedIds;
  }

  if (allowedBusinessIds === null) {
    return null;
  }

  if (hasGlobalBusinessAccess) {
    return null;
  }

  if (isBusiness && businessId) {
    return [businessId];
  }

  return [];
};

export const canAccessBusiness = (context = {}, candidateBusinessId) => {
  if (!context?.capabilities?.canViewBusinesses) {
    return false;
  }

  if (context?.allowedBusinessIds === null) {
    return true;
  }

  const normalizedBusinessId = normalizeScopedId(candidateBusinessId);
  return Boolean(
    normalizedBusinessId &&
      Array.isArray(context?.allowedBusinessIds) &&
      context.allowedBusinessIds.includes(normalizedBusinessId)
  );
};

export const resolveAccessContext = (access = {}) => {
  const normalizedAccess = normalizeAccessInput(access);
  const role = normalizedAccess.authBypassed
    ? ROLE_ADMIN
    : normalizedAccess.role;
  const capabilities = resolveCapabilities(normalizedAccess);
  const clientId = normalizeScopedId(access?.clientId);
  const businessId = normalizeScopedId(access?.businessId);
  const isAdmin = role === ROLE_ADMIN;
  const isClient = role === ROLE_CLIENT;
  const isBusiness = role === ROLE_BUSINESS;
  const hasGlobalBusinessAccess = isAdmin || Boolean(normalizedAccess.authBypassed);
  const allowedBusinessIds = normalizeAllowedBusinessIds({
    allowedBusinessIds: access?.allowedBusinessIds,
    isBusiness,
    businessId,
    hasGlobalBusinessAccess,
  });

  const context = {
    role,
    clientId,
    businessId,
    allowedBusinessIds,
    capabilities,
    authBypassed: Boolean(normalizedAccess.authBypassed),
    accessContextReady: Boolean(access?.accessContextReady),
    isAdmin,
    isClient,
    isBusiness,
    isScopedUser: isClient || isBusiness,
    hasClientScope: clientId !== null,
    hasBusinessScope: businessId !== null,
  };

  return {
    ...context,
    hasScopedAccess:
      (context.isClient && context.hasClientScope) ||
      (context.isBusiness && context.hasBusinessScope),
  };
};
